import type { SourceItem } from '@paladigm/answer-visuals';
import i18n from 'i18next';

import { formatFileSize } from '@shared/lib/file-format';

import { getAttachmentPolicy } from '../data/attachment-policy';
import { shownModel } from '../data/models';
import { levelOffer, levelToSend } from '../data/thinking-levels';
import { tr, uiLanguage } from '../i18n';
import { CONNECT_CARD_SCHEMA } from '../types';
import type {
  AttachedFile,
  AttachedRef,
  ChatService,
  ContentBlock,
  ContextFigure,
  DataNote,
  DeleteOutcome,
  InteractionAnswer,
  InteractionProposal,
  InteractionQuestion,
  LiveSession,
  ProposalDecision,
  RanWith,
  SendOptions,
  StepHealth,
  StreamChunk,
  ThinkingControls,
  TranscriptTurn,
  TurnAnswer,
  UploadOptions,
} from '../types';
import { renderContext } from '../visuals';

import {
  OrchestratorError,
  attachFile,
  clearSessions,
  compactSession as compactOrchestratorSession,
  createSession,
  deleteSession as deleteOrchestratorSession,
  deleteUploadedFile,
  fetchModels,
  getSession,
  getTranscript,
  removeQueued as removeQueuedMessage,
  resolveSelectableModel,
  selectableModel,
  stopTurn as stopOrchestratorTurn,
  streamMessage,
  transcribe as transcribeAudio,
  uploadFileWithProgress,
} from './orchestrator-client';
import type {
  ArchivedTranscript,
  MessageAnswer,
  MessageRequest,
  ModelsResponse,
  SessionTranscript,
  SseEvent,
} from './orchestrator-client';
import {
  ORCHESTRATOR_CAPABILITIES,
  WIRE,
  readBlockOrder,
  readOutputFailure,
  readPreparingOutputs,
  readTranscriptBlocks,
  readTranscriptFiles,
  readVaultFileId,
  readVaultSave,
} from './wire';

/** The action code that terminates a turn (FRONTEND_INTEGRATION §5.4). */
const DONE_CODE = 'SESSION_COMPLETE';
/** The status line: one line of plain text the orchestrator composes. */
const STATUS_CODE = 'STATUS';
/** The turn took queued messages: at its opening, or mid-turn. */
const MESSAGE_TAKEN_CODE = 'MESSAGE_TAKEN';
/**
 * The user stopped the turn; its stop card follows, or, when nothing had finished, one fixed
 * line that says so, which is the stopped reply's text.
 */
const TURN_STOPPED_CODE = 'TURN_STOPPED';
/** The thinking level and agentic the turn runs with, the level asked and what changed it. */
const TURN_MODE_CODE = 'TURN_MODE';
/** The session read a connected account's content: it runs the deployment's model from here on. */
const SESSION_CONNECTED_CODE = 'SESSION_CONNECTED';
/** A tool call started: its `label` and `subject` name the step a gap is shown under. */
const TASK_STARTED_CODE = 'TASK_STARTED';
/** A tool call succeeded: its `data_state`, `notes` and `sources` say what came back. */
const TASK_COMPLETED_CODE = 'TASK_COMPLETED';
/** The stop card's flow, and its "Give me what you have so far" (see cardFields). */
const STOP_CARD_FLOW = 'turn_stop_v1';
const STOP_PARTIAL = 'partial';

/** Dev-only trace of the SSE→chunk mapping (toggle: localStorage.aiCioDebug='1'). */
function dbg(...args: unknown[]): void {
  if (import.meta.env.DEV && localStorage.getItem('aiCioDebug') === '1') {
    console.debug('[ai-cio]', ...args);
  }
}

/** Coerce a wire value to a string without "[object Object]". */
function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

export function toContentBlock(data: Record<string, unknown>): ContentBlock | undefined {
  const id = typeof data.id === 'string' ? data.id : undefined;
  const type = typeof data.type === 'string' ? data.type : undefined;
  if (!id || !type) return undefined;
  const rawMeta: unknown = data.metadata;
  const meta =
    typeof rawMeta === 'object' && rawMeta !== null ? (rawMeta as Record<string, unknown>) : {};
  const taskId = str(data[WIRE.taskId]);
  const outputId = str(meta[WIRE.blockOutputId]);
  const vaultFileId = readVaultFileId(meta[WIRE.blockVaultFileId]);
  return {
    id,
    type: type as ContentBlock['type'],
    metadata: data.metadata as Record<string, unknown> | undefined,
    payload: data.payload as Record<string, unknown> | undefined,
    ...(taskId ? { taskId } : {}),
    ...(outputId ? { outputId } : {}),
    ...(vaultFileId ? { vaultFileId } : {}),
  };
}

function toInteraction(data: Record<string, unknown>): InteractionQuestion | undefined {
  const prompt = str(data.prompt) || str(data.plain_text);
  const optionsRaw = Array.isArray(data.options) ? data.options : [];
  const options = optionsRaw
    .map((o): InteractionQuestion['options'][number] | null => {
      const opt = o as Record<string, unknown>;
      const id = typeof opt.id === 'string' ? opt.id : undefined;
      if (!id) return null;
      return {
        id,
        label: str(opt.label),
        isFreeForm: Boolean(opt.is_free_form),
      };
    })
    .filter((o): o is InteractionQuestion['options'][number] => o !== null);

  // Surface the question if there is anything to render. Don't hard-require
  // intent_id/question_id — default them so a payload that omits them still
  // renders (the answer path forwards whatever ids we have).
  if (!prompt && options.length === 0) return undefined;

  return {
    intentId: str(data.intent_id),
    flow: str(data.flow) || 'mcq',
    questionId: str(data.question_id),
    prompt,
    options,
  };
}

function toProposal(data: Record<string, unknown>): InteractionProposal | undefined {
  const proposalId = str(data.proposal_id);
  if (!proposalId) return undefined;

  const plainText = str(data.plain_text);
  const title = str(data.title);
  const content =
    typeof data.content === 'object' && data.content !== null
      ? (data.content as Record<string, unknown>)
      : {};
  if (!title && !plainText && Object.keys(content).length === 0) return undefined;

  return {
    intentId: str(data.intent_id),
    flow: str(data.flow) || 'proposal',
    proposalId,
    title,
    schema: str(data.schema),
    content,
    rationale: str(data.rationale),
    tradeoffs: Array.isArray(data.tradeoffs)
      ? data.tradeoffs.filter((t): t is string => typeof t === 'string')
      : [],
    plainText,
  };
}

/** A wire string with text in it, trimmed, or undefined. */
function filled(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/** A turn id as the wire names one: a finite number, else none. */
function turnOf(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * A `context` (the session view's, SESSION_COMPLETE's) as the meter reads it: null for none, and
 * for one it cannot read; a window that is not a positive number reads as none.
 */
function contextFigure(value: unknown): ContextFigure | null {
  if (!value || typeof value !== 'object') return null;
  const c = value as { used_tokens?: unknown; window_tokens?: unknown; estimated?: unknown };
  if (typeof c.used_tokens !== 'number' || !Number.isFinite(c.used_tokens)) return null;
  const window =
    typeof c.window_tokens === 'number' && Number.isFinite(c.window_tokens) && c.window_tokens > 0
      ? c.window_tokens
      : null;
  return { usedTokens: c.used_tokens, windowTokens: window, estimated: c.estimated === true };
}

/**
 * What a SESSION_COMPLETE or a session view says of the meter and the divider, each only when
 * sent: an older orchestrator sends neither, and a frame that does not say is no source.
 */
function compactionFields(data: object): Pick<StreamChunk, 'context' | 'compactedThroughTurn'> {
  return {
    ...('context' in data ? { context: contextFigure(data.context) } : {}),
    ...('compacted_through_turn' in data
      ? { compactedThroughTurn: turnOf(data.compacted_through_turn) }
      : {}),
  };
}

/** TASK_COMPLETED's `notes`: each `{code, text}` with a code and some text; the rest dropped. */
function toNotes(raw: unknown): DataNote[] {
  return (Array.isArray(raw) ? raw : []).flatMap((n): DataNote[] => {
    if (!n || typeof n !== 'object') return [];
    const note = n as Record<string, unknown>;
    const code = filled(note.code);
    const body = filled(note.text);
    return code && body ? [{ code, text: body }] : [];
  });
}

/**
 * TASK_COMPLETED's `sources`, in the renderer package's `SourceItem` shape: each with a title,
 * the optional fields kept only when they are what the shape says.
 */
function toSources(raw: unknown): SourceItem[] {
  return (Array.isArray(raw) ? raw : []).flatMap((s): SourceItem[] => {
    if (!s || typeof s !== 'object') return [];
    const source = s as Record<string, unknown>;
    const title = filled(source.title);
    if (!title) return [];
    const publisher = filled(source.publisher);
    const date = filled(source.date);
    const url = filled(source.url);
    return [
      {
        title,
        ...(publisher ? { publisher } : {}),
        ...(date ? { date } : {}),
        ...(source.through === true ? { through: true } : {}),
        ...(url ? { url } : {}),
      },
    ];
  });
}

/**
 * What a TASK_COMPLETED says came back, or undefined when it carries none of `data_state`,
 * `notes` and `sources` (an orchestrator that predates them, or one with the presentation
 * switch off): such a frame maps to nothing, as before. `step` names the call as its
 * TASK_STARTED did; a tool's name is never kept.
 */
function toOutcome(
  data: Record<string, unknown>,
  step: string | undefined,
): Pick<StreamChunk, 'health' | 'sources'> | undefined {
  const dataState = filled(data.data_state);
  const notes = toNotes(data.notes);
  const sources = toSources(data.sources);
  if (!dataState && notes.length === 0 && sources.length === 0) return undefined;
  const health: StepHealth | undefined =
    dataState || notes.length > 0
      ? { ...(step ? { step } : {}), ...(dataState ? { dataState } : {}), notes }
      : undefined;
  return { ...(health ? { health } : {}), ...(sources.length > 0 ? { sources } : {}) };
}

/** MESSAGE_TAKEN's `messages`: the queued messages a turn took, by their queue ids. */
function toTaken(raw: unknown): { id: string; text: string }[] {
  return (Array.isArray(raw) ? raw : []).flatMap((m): { id: string; text: string }[] => {
    const message = m as Record<string, unknown>;
    const id = str(message.id);
    return id ? [{ id, text: str(message.text) }] : [];
  });
}

/**
 * Maps an orchestrator SSE event stream onto the StreamChunk seam. Shared by
 * the message, interaction-answer and drain paths.
 *
 * The turn fails only on an `error` frame, an `error` on SESSION_COMPLETE, or a
 * stream that ends without SESSION_COMPLETE. A TASK_FAILED is not read: the
 * model recovers from a failed tool call and answers, and that answer is not an
 * error.
 *
 * A TASK_COMPLETED's `data_state`, `notes` and `sources` are kept for the reply's data health
 * and Sources footer (see toOutcome), each step named by its TASK_STARTED's label.
 *
 * TURN_MODE is read only when `readsMode` (see thinkingFields): its level, with `ranModel` (the
 * request's model as this client resolves it), is what the answer ran with.
 */
export async function* mapStream(
  events: AsyncGenerator<SseEvent>,
  sessionId: string,
  readsMode = false,
  ranModel?: RanWith['model'],
): AsyncIterable<StreamChunk> {
  let emittedSession = false;
  // An `error` frame is not terminal: SESSION_COMPLETE still follows it.
  let errored = false;
  // The turn has started its work: a MESSAGE_TAKEN from here on is an intake into it,
  // before it one names the messages the turn opens with.
  let opened = false;
  // Each call's step as its TASK_STARTED named it ("Calculating performance · Q3"), by task id.
  const steps = new Map<string, string>();

  try {
    for await (const evt of events) {
      dbg('event', evt.event, evt.event === 'action' ? evt.data.code : '');
      if (!emittedSession) {
        emittedSession = true;
        yield { delta: '', done: false, sessionId };
      }

      switch (evt.event) {
        case 'delta': {
          opened = true;
          const content = typeof evt.data.content === 'string' ? evt.data.content : '';
          if (content) yield { delta: content, done: false };
          break;
        }
        case 'block_end': {
          opened = true;
          const block = toContentBlock(evt.data);
          if (block) yield { delta: '', done: false, block };
          break;
        }
        case 'message_manifest': {
          const ids = Array.isArray(evt.data.block_ids)
            ? evt.data.block_ids.filter((x): x is string => typeof x === 'string')
            : [];
          if (ids.length) yield { delta: '', done: false, blockOrder: ids };
          break;
        }
        case 'interaction_question': {
          opened = true;
          const interaction = toInteraction(evt.data);
          dbg('interaction mapped?', Boolean(interaction), interaction?.options.length, 'opts');
          if (interaction) yield { delta: '', done: false, interaction };
          break;
        }
        case 'interaction_proposal': {
          opened = true;
          const proposal = toProposal(evt.data);
          dbg('proposal mapped?', Boolean(proposal), proposal?.schema);
          if (proposal) yield { delta: '', done: false, proposal };
          break;
        }
        case 'interaction_resolved': {
          // A card was consumed, or closed by this turn (a stop card a new message moved on from).
          const resolved = str(evt.data.target_id);
          if (resolved) yield { delta: '', done: false, resolved };
          break;
        }
        case 'error': {
          errored = true;
          break;
        }
        case WIRE.vaultSavedEvent: {
          const save = readVaultSave(evt.data);
          if (save) yield { delta: '', done: false, vaultSaves: [save] };
          break;
        }
        case WIRE.outputFailedEvent: {
          const failure = readOutputFailure(evt.data);
          if (failure) yield { delta: '', done: false, outputFailures: [failure] };
          break;
        }
        case 'action': {
          const code = evt.data.code;
          if (code === STATUS_CODE) {
            // Shown as it is: the server composes it and never names a tool.
            const status = str(evt.data.text).trim();
            if (status) yield { delta: '', done: false, status };
            break;
          }
          if (code === MESSAGE_TAKEN_CODE) {
            const taken = toTaken(evt.data.messages);
            if (taken.length) yield { delta: '', done: false, taken, intake: opened };
            break;
          }
          if (code === TURN_STOPPED_CODE) {
            yield { delta: '', done: false, stopped: true };
            break;
          }
          if (code === TURN_MODE_CODE) {
            // Said only when the family of the turn's model runs another rank than was asked.
            // Shown as the server reports it, never compared with the control or the request:
            // a platform's ceiling is no news.
            if (readsMode && evt.data.changed_by === 'model') {
              yield {
                delta: '',
                done: false,
                modeNote: {
                  level: str(evt.data.thinking_level),
                  label: str(evt.data.label),
                  requested: str(evt.data.requested_level),
                  requestedLabel: str(evt.data.requested_label),
                },
              };
            }
            // Sent once per turn: what the answer ran with, for its meta line.
            if (readsMode) {
              const level = str(evt.data.thinking_level);
              yield {
                delta: '',
                done: false,
                ran: {
                  ...(ranModel ? { model: ranModel } : {}),
                  level: level ? { id: level, label: str(evt.data.label) } : null,
                },
              };
            }
            break;
          }
          if (code === SESSION_CONNECTED_CODE) {
            yield { delta: '', done: false, connected: true };
            break;
          }
          if (code === TASK_STARTED_CODE) {
            const taskId = str(evt.data.task_id);
            const label = filled(evt.data.label);
            if (taskId && label) {
              const subject = filled(evt.data.subject);
              steps.set(taskId, subject ? `${label} · ${subject}` : label);
            }
            const preparing = readPreparingOutputs(evt.data);
            if (preparing.length) yield { delta: '', done: false, preparing };
            break;
          }
          if (code === TASK_COMPLETED_CODE) {
            const outcome = toOutcome(evt.data, steps.get(str(evt.data.task_id)));
            if (outcome) yield { delta: '', done: false, ...outcome };
            break;
          }
          if (code === WIRE.vaultSavedAction) {
            const save = readVaultSave(evt.data);
            if (save) yield { delta: '', done: false, vaultSaves: [save] };
            break;
          }
          if (code === WIRE.outputFailedAction) {
            const failure = readOutputFailure(evt.data);
            if (failure) yield { delta: '', done: false, outputFailures: [failure] };
            break;
          }
          if (code === 'PARSING_INTENTS' || code === 'TURN_RESUMED' || code === 'STREAM_START') {
            opened = true;
            break;
          }
          if (code === DONE_CODE) {
            // The record the turn ended with, when the request persisted one; the meter's
            // figure and the cut as the turn ended.
            const turnId = turnOf(evt.data.turn_id);
            yield {
              delta: '',
              done: true,
              error: errored || Boolean(evt.data.error),
              ...(typeof evt.data.queued === 'number' ? { queued: evt.data.queued } : {}),
              ...(evt.data.queue_held === true ? { queueHeld: true } : {}),
              ...(turnId !== null ? { turnId } : {}),
              ...compactionFields(evt.data),
            };
            return;
          }
          break;
        }
        default:
          break;
      }
    }
    // The stream closed without SESSION_COMPLETE: the turn was cut off.
    yield { delta: '', done: true, error: true };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    yield { delta: '', done: true, error: true };
  }
}

interface RefusedFile {
  name: string;
  reason: string;
  /** The orchestrator answered 4xx: the file itself is refused, not a failure worth retrying. */
  refused: boolean;
}

/**
 * Why a file did not reach the session: the orchestrator's own reason on a 4xx
 * (a type or size it does not accept, a file it no longer has), otherwise a
 * failure worth retrying (a 5xx or the network).
 */
function isRefusal(err: unknown): err is OrchestratorError {
  return err instanceof OrchestratorError && err.status >= 400 && err.status < 500;
}

function refusalLimit(limitBytes: number): string {
  return formatFileSize(limitBytes, i18n.t.bind(i18n), uiLanguage());
}

function refusalCopy(err: OrchestratorError): string | undefined {
  switch (err.code) {
    case 'type_not_allowed':
      return tr('upload.refused.typeNotAllowed', 'This file type isn’t accepted.');
    case 'too_large':
      return tr('upload.refused.tooLarge', 'This file is larger than the {{limit}} limit.', {
        limit: refusalLimit(err.limitBytes ?? getAttachmentPolicy().maxBytes),
      });
    case 'empty_file':
      return tr('upload.refused.emptyFile', 'This file is empty.');
    default:
      return undefined;
  }
}

function refusalReason(err: unknown): string {
  if (isExpiredFileRef(err)) {
    return tr('upload.refused.expired', 'This file is no longer available. Attach it again.');
  }
  if (isRefusal(err)) return refusalCopy(err) ?? err.message;
  return tr('upload.unavailable', 'The upload could not be completed right now. Please try again.');
}

/** Backslash-escapes the markdown that inline text could trigger, so it renders as typed. */
function escapeMarkdown(text: string): string {
  return text.replace(/[\\`*_[\]<>~]/g, '\\$&');
}

/**
 * The error reply for a turn that was not sent because files were refused. The
 * files that did attach stay on the session, and the next turn sees them, so the
 * reply says so rather than have them uploaded twice.
 */
function refusedFilesMessage(refused: RefusedFile[], othersAttached: boolean): string {
  const lines = refused.map(
    ({ name, reason }) => `- **${escapeMarkdown(name)}**: ${escapeMarkdown(reason)}`,
  );
  return [
    tr('upload.notSent', 'Your message was not sent. The AI CIO could not attach:'),
    lines.join('\n'),
    // Only a refused file needs dropping or replacing; a failed one is retried as is.
    ...(refused.some((r) => r.refused)
      ? [
          tr(
            'upload.sendAgain',
            'Send your message again without the file(s) above, or replace them with files the AI CIO accepts.',
          ),
        ]
      : []),
    ...(othersAttached
      ? [
          tr(
            'upload.othersAttached',
            'The other files were attached, so there is no need to add them again.',
          ),
        ]
      : []),
  ].join('\n\n');
}

/**
 * The attach step's answer for a session it no longer has. The same route also
 * answers 404 and 403 for the file itself, and /files/upload never reads a
 * session, so only this detail means the session is stale.
 */
function isStaleSessionAtAttach(err: unknown): boolean {
  return (
    err instanceof OrchestratorError && err.status === 404 && err.message === 'Session not found'
  );
}

function isExpiredFileRef(err: unknown): boolean {
  return err instanceof OrchestratorError && err.status === 404 && !isStaleSessionAtAttach(err);
}

type PendingAttachment = Pick<AttachedFile, 'file' | 'fileRef'>;

async function attachToSession(
  sessionId: string,
  pending: PendingAttachment,
  signal?: AbortSignal,
): Promise<{ fileRef: string; vaultFileId?: string } | undefined> {
  if (pending.fileRef) {
    try {
      return await attachWithBusyRetry(sessionId, pending.fileRef, signal);
    } catch (err) {
      if (!pending.file || !isExpiredFileRef(err)) throw err;
    }
  }
  if (!pending.file) return undefined;
  pending.fileRef = await uploadFileWithProgress(pending.file, { signal });
  return attachWithBusyRetry(sessionId, pending.fileRef, signal);
}

/**
 * What a message, a queued send, a drain or a stop card's "Something else" carries of the
 * chat's thinking choice (iq-ai-cio `docs/features/thinking-levels.md`, "Requests"): the id of
 * the level the control shows for the model the request runs on (the chat's model, the
 * deployment's in a connected session, the partner's under an override; see shownModel), none
 * for a model that takes no level, or an older orchestrator's name for it; and always
 * `agentic`, the switch's value, false when it is off. `controls` are the chat's choice and
 * switch: as the request carries them (`SendOptions`), or as they were when the user answered
 * the card. `readsMode`: the /models the fields were computed from carried `thinking_scale`, so
 * the turn's TURN_MODE note is shown. Every other card answer carries neither field and reads no TURN_MODE (see
 * cardFields). `models` is the same /models response the request's model is chosen from, so a
 * request reads /models once. `ranModel`: that model, which the answer's meta line names.
 */
function thinkingFields(
  options: SendOptions,
  models: ModelsResponse,
  controls: ThinkingControls = {
    choice: options.thinking ?? null,
    agentic: options.agentic === true,
  },
): {
  fields: Pick<MessageRequest, 'thinking_level' | 'agentic'>;
  readsMode: boolean;
  ranModel?: RanWith['model'];
} {
  const shown = shownModel(models, options.modelId, {
    sessionModelUnknown: options.sessionModelUnknown,
    connected: options.connected,
  });
  const offer = levelOffer(models.thinking_scale, shown ? shown.thinking : undefined);
  const level = levelToSend(offer, controls.choice, controls.agentic);
  return {
    fields: {
      ...(level !== null ? { thinking_level: level } : {}),
      agentic: controls.agentic,
    },
    readsMode: models.thinking_scale !== undefined,
    ...(shown ? { ranModel: { label: shown.label, group: shown.group } } : {}),
  };
}

/**
 * What a card answer carries besides the answer itself: the model and, on a stop card's
 * "Something else" alone (any selection but `partial`, with or without text), the thinking
 * fields. That answer runs what the user asks next, so it carries what a send would carry
 * with the level and switch the chat had when the user answered, and its TURN_MODE
 * note is shown. Every other answer, "Give me what you have so far" among them, carries
 * neither field: its turn keeps the stopped turn's or the card's own mode, and its TURN_MODE
 * is not read.
 */
async function cardFields(
  { question, selection, controls }: InteractionAnswer,
  options: SendOptions,
): Promise<{
  fields: Pick<MessageRequest, 'model' | 'thinking_level' | 'agentic'>;
  readsMode: boolean;
  ranModel?: RanWith['model'];
}> {
  if (question.flow !== STOP_CARD_FLOW || selection === STOP_PARTIAL) {
    return { fields: { model: await resolveSelectableModel(options.modelId) }, readsMode: false };
  }
  const models = await fetchModels();
  const thinking = thinkingFields(options, models, controls);
  return {
    fields: { model: selectableModel(options.modelId, models), ...thinking.fields },
    readsMode: thinking.readsMode,
    ranModel: thinking.ranModel,
  };
}

function toolsetFields(options: SendOptions): Pick<MessageRequest, 'client_context'> {
  return options.blockedToolsets?.length
    ? { client_context: { toolsets: { disabled: options.blockedToolsets } } }
    : {};
}

/**
 * The real {@link ChatService}, backed by the IQ AI CIO orchestrator
 * (REST + SSE). Maps the orchestrator's event stream onto the StreamChunk seam
 * the store consumes. Swap it in with `setChatService(createOrchestratorChatService())`.
 */
export function createOrchestratorChatService(): ChatService {
  return {
    async *streamReply(prompt: string, options: SendOptions): AsyncIterable<StreamChunk> {
      const models = await fetchModels();
      const model = selectableModel(options.modelId, models);
      const language = uiLanguage();
      const thinking = thinkingFields(options, models);
      let sessionId = options.sessionId;
      if (!sessionId) {
        sessionId = await createSession({ model, language }, options.signal);
      }
      if (options.files?.length) {
        // A file the orchestrator refuses never reaches the session, so sending
        // the turn anyway would have it answered as if that file were attached.
        // Try every file, then stop and name each refused one with its reason,
        // unless the session stays busy at one: the request is then not taken.
        const refused: RefusedFile[] = [];
        const attachedRefs: AttachedRef[] = [];
        let attachedCount = 0;
        let busy = false;
        // A persisted session the orchestrator no longer has fails the attach
        // step the way it fails a message. Recover as sendWithRecovery does, once,
        // into a fresh session — or an old chat could never send a file again.
        let canRecover = Boolean(options.sessionId);
        for (const attached of options.files) {
          if (!attached.file && !attached.fileRef) continue;
          const pending: PendingAttachment = { file: attached.file, fileRef: attached.fileRef };
          try {
            let result: Awaited<ReturnType<typeof attachToSession>>;
            try {
              result = await attachToSession(sessionId, pending, options.signal);
            } catch (err) {
              if (!canRecover || !isStaleSessionAtAttach(err)) throw err;
              canRecover = false;
              sessionId = await createSession({ model, language }, options.signal);
              result = await attachToSession(sessionId, pending, options.signal);
            }
            attachedCount += 1;
            if (result) {
              attachedRefs.push({
                attachmentId: attached.id,
                fileRef: result.fileRef,
                ...(result.vaultFileId ? { vaultFileId: result.vaultFileId } : {}),
              });
            }
            // The session took a file, so it is not stale, and moving to a fresh
            // one now would leave that file behind.
            canRecover = false;
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') throw err;
            if (refusalAnswer(err)?.status === 'busy') {
              busy = true;
              break;
            }
            dbg('file upload failed', attached.name, err);
            refused.push({
              name: attached.name,
              reason: refusalReason(err),
              refused: isRefusal(err),
            });
          }
        }
        if (refused.length && !busy) {
          yield {
            delta: refusedFilesMessage(refused, attachedCount > 0),
            done: true,
            error: true,
            filesRefused: true,
            sessionId,
            ...(attachedRefs.length ? { attached: attachedRefs } : {}),
          };
          return;
        }
        if (attachedRefs.length) {
          yield { delta: '', done: false, sessionId, attached: attachedRefs };
        }
        if (busy) {
          yield { delta: '', done: true, answer: { status: 'busy' } };
          return;
        }
      }
      yield* sendWithRecovery(
        sessionId,
        {
          message: prompt,
          model,
          language,
          ...thinking.fields,
          ...toolsetFields(options),
          // While a turn runs in the session, the message is queued behind it.
          ...(options.clientMessageId
            ? { queue: true, client_message_id: options.clientMessageId }
            : {}),
        },
        options.signal,
        { readsMode: thinking.readsMode, ranModel: thinking.ranModel },
      );
    },

    async uploadFile(file: File, options?: UploadOptions): Promise<{ fileRef: string }> {
      return { fileRef: await uploadFileWithProgress(file, options) };
    },

    ...(ORCHESTRATOR_CAPABILITIES.deleteFile
      ? { deleteFile: (fileRef: string) => deleteUploadedFile(fileRef) }
      : {}),

    async *answerInteraction(
      answer: InteractionAnswer,
      options: SendOptions,
    ): AsyncIterable<StreamChunk> {
      const sessionId = options.sessionId;
      if (!sessionId) {
        // Can't answer a question without the session it was asked in.
        yield { delta: '', done: true, error: true };
        return;
      }
      // Answer through the INTERACTION route, the same way decideProposal does.
      // This used to send a plain {message}, relying on a legacy auto-detect that
      // resolved any message arriving while a question was open. The pure path has
      // no such auto-detect: it looks up an interaction only when `interaction` is
      // present in the body (api/v1/sessions.py), so a plain message started a
      // fresh turn and the question was left open forever.
      //
      // `decision: 'select'` is what the orchestrator's ask_user resume arm keys
      // on; 'approve' takes the decline template. `selection` is the chosen
      // option's id and `text` rides only for a free-form option — the server
      // resolves the label from its own snapshot, so nothing about what the user
      // saw depends on what this client sends back.
      //
      // `queue` lets the answer's turn take queued messages in, and a stop card's
      // "Something else" run the queue with it. The answer itself is never queued.
      //
      // `thinking_level` and `agentic` ride only on a stop card's "Something else", whose
      // TURN_MODE is read as a send's; every other card's turn runs in the mode it was
      // raised under (see cardFields). The mode card a typed phrase raises and the offer of a
      // deeper pass (flow `turn_mode_v1`) are answered here like any other question card.
      const chosen = answer.question.options.find((o) => o.id === answer.selection);
      const card = await cardFields(answer, options);
      yield* sendWithRecovery(
        sessionId,
        {
          interaction: {
            intent_id: answer.question.intentId,
            flow: answer.question.flow,
            target_id: answer.question.questionId,
            decision: 'select',
            selection: answer.selection,
            ...(chosen?.isFreeForm && answer.text?.trim() ? { text: answer.text.trim() } : {}),
          },
          queue: true,
          language: uiLanguage(),
          ...card.fields,
          ...toolsetFields(options),
        },
        options.signal,
        { retryOnStaleSession: false, readsMode: card.readsMode, ranModel: card.ranModel },
      );
    },

    async *decideProposal(
      { proposal, decision }: ProposalDecision,
      options: SendOptions,
    ): AsyncIterable<StreamChunk> {
      const sessionId = options.sessionId;
      if (!sessionId) {
        yield { delta: '', done: true, error: true };
        return;
      }
      const model = await resolveSelectableModel(options.modelId);
      yield* sendWithRecovery(
        sessionId,
        {
          interaction: {
            intent_id: proposal.intentId,
            flow: proposal.flow,
            target_id: proposal.proposalId,
            decision,
          },
          queue: true,
          model,
          language: uiLanguage(),
          ...toolsetFields(options),
        },
        options.signal,
        {
          retryOnStaleSession: false,
          // A Connect card answers when its Connect ends, maybe after another tab answered it
          // or a newer message closed it: that 409 means the card is done (CC D15).
          closedOnRefusal: proposal.schema === CONNECT_CARD_SCHEMA,
        },
      );
    },

    async *drainQueue(options: SendOptions): AsyncIterable<StreamChunk> {
      const sessionId = options.sessionId;
      if (!sessionId) {
        yield { delta: '', done: true, answer: { status: 'empty' } };
        return;
      }
      // Carries what a send would carry now, so the queued messages run as if sent.
      const models = await fetchModels();
      const thinking = thinkingFields(options, models);
      yield* sendWithRecovery(
        sessionId,
        {
          queue: true,
          model: selectableModel(options.modelId, models),
          language: uiLanguage(),
          ...thinking.fields,
          ...toolsetFields(options),
        },
        options.signal,
        {
          retryOnStaleSession: false,
          readsMode: thinking.readsMode,
          ranModel: thinking.ranModel,
        },
      );
    },

    stopTurn(sessionId: string): Promise<boolean> {
      return stopOrchestratorTurn(sessionId);
    },

    removeQueued(sessionId: string, messageId: string): Promise<boolean> {
      return removeQueuedMessage(sessionId, messageId);
    },

    async readSession(sessionId: string, signal?: AbortSignal): Promise<LiveSession | null> {
      let session: SessionTranscript;
      try {
        session = await getSession(sessionId, signal);
      } catch (err) {
        if (err instanceof OrchestratorError && err.status === 404) return null;
        throw err;
      }
      const queue = session.queue ?? [];
      const stopCard = session.stop_card ? toInteraction(session.stop_card.payload) : undefined;
      return {
        turns: fromLive(session, sessionId),
        turnRunning: session.turn_running === true,
        queue: queue.map((entry) => ({
          id: entry.id,
          text: entry.text,
          createdAt: Date.parse(entry.created_at) || Date.now(),
        })),
        ...(stopCard ? { stopCard } : {}),
        ...(session.queue_held === true ? { queueHeld: true } : {}),
        ...(session.mode_card_open === true ? { modeCardOpen: true } : {}),
        ...(session.connected === true ? { connected: true } : {}),
        ...(session.compacting === true ? { compacting: true } : {}),
        ...compactionFields(session),
      };
    },

    transcribe(audio: Blob, language?: string, signal?: AbortSignal): Promise<string> {
      return transcribeAudio(audio, language, signal);
    },

    async compactSession(sessionId: string, modelId: string, signal?: AbortSignal) {
      // The `model` the chat's next message would send, none where it sends none.
      return compactOrchestratorSession(sessionId, await resolveSelectableModel(modelId), signal);
    },

    async loadTranscript(sessionId: string, signal?: AbortSignal): Promise<TranscriptTurn[]> {
      try {
        return fromArchive(await getTranscript(sessionId, signal));
      } catch (err) {
        // Only a saved session is archived (not a connected chat after its switch, nor
        // one opened while saving was off), so a 404 may still be a live session within
        // its TTL. A deleted one 404s there too.
        if (!(err instanceof OrchestratorError && err.status === 404)) throw err;
      }
      return fromLive(await getSession(sessionId, signal), sessionId, Date.now());
    },

    deleteSession(sessionId: string): Promise<DeleteOutcome> {
      // 404: the live session is another user's, so nothing of this user's is left to erase.
      return withBusyRetry(() => deleteOrchestratorSession(sessionId), {
        notFoundIsDeleted: true,
        stop: () => stopOrchestratorTurn(sessionId),
      });
    },

    clearHistory(running: string[] = []): Promise<DeleteOutcome> {
      return withBusyRetry(() => clearSessions(), {
        notFoundIsDeleted: false,
        stop: () => Promise.all(running.map((sessionId) => stopOrchestratorTurn(sessionId))),
      });
    },
  };
}

/**
 * Waits before each retry of a delete or an attach refused with 409 `turn_in_progress`. A
 * turn runs to its end whoever stops reading it, so a delete asks the turn to stop first: it
 * ends at its next safe point. An attach is refused only while an operation holds the session,
 * or as a turn ends. A short wait usually clears either.
 */
const BUSY_RETRY_DELAYS_MS = [500, 1000, 2000];

/**
 * Runs a server-side delete and maps how it ended: 'busy' when a turn still holds
 * the conversation after the retries, 'failed' on any other error (a 5xx, the
 * network, a route the orchestrator does not have). Before each retry it calls
 * `stop`, which asks the running turn to stop; a failed stop still retries.
 */
async function withBusyRetry(
  run: () => Promise<void>,
  opts: { notFoundIsDeleted: boolean; stop: () => Promise<unknown> },
): Promise<DeleteOutcome> {
  for (let attempt = 0; ; attempt++) {
    try {
      await run();
      return 'deleted';
    } catch (err) {
      const status = err instanceof OrchestratorError ? err.status : 0;
      if (status === 404 && opts.notFoundIsDeleted) return 'deleted';
      if (status !== 409) return 'failed';
      const delay = BUSY_RETRY_DELAYS_MS[attempt];
      if (delay === undefined) return 'busy';
      await opts.stop().catch(() => undefined);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

async function attachWithBusyRetry(
  sessionId: string,
  fileRef: string,
  signal?: AbortSignal,
): ReturnType<typeof attachFile> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await attachFile(sessionId, fileRef, signal);
    } catch (err) {
      if (refusalAnswer(err)?.status !== 'busy') throw err;
      const delay = BUSY_RETRY_DELAYS_MS[attempt];
      if (delay === undefined) throw err;
      await sleep(delay, signal);
    }
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const id = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function userTurn(
  content: string,
  files: AttachedFile[],
  id: Pick<TranscriptTurn, 'turnId'>,
): TranscriptTurn[] {
  if (!content.trim() && files.length === 0) return [];
  return [
    {
      role: 'user',
      content: content.trim() ? content : '',
      ...(files.length ? { files } : {}),
      ...id,
    },
  ];
}

function assistantTurn(
  content: string,
  blocks: ContentBlock[],
  blockOrder: string[] | undefined,
  extra: Pick<TranscriptTurn, 'turnId' | 'stopped'>,
): TranscriptTurn[] {
  if (!content.trim() && blocks.length === 0 && !extra.stopped) return [];
  return [
    {
      role: 'assistant',
      content: content.trim() ? content : '',
      ...(blocks.length ? { blocks } : {}),
      ...(blocks.length && blockOrder ? { blockOrder } : {}),
      ...extra,
    },
  ];
}

/**
 * An archived turn is a user message and its reply; either half may be empty. Both carry the
 * turn's `turn_index`, its record's turn id.
 */
export function fromArchive(
  transcript: ArchivedTranscript,
  now: number = Date.now(),
): TranscriptTurn[] {
  const turns: TranscriptTurn[] = [];
  const archived = Array.isArray(transcript.turns) ? transcript.turns : [];
  for (const [index, turn] of archived.entries()) {
    const turnId = turnOf(turn.turn_index);
    const id = turnId !== null ? { turnId } : {};
    const prefix = `${str(transcript.session_id)}:${index.toString()}`;
    turns.push(
      ...userTurn(str(turn.user_text), readTranscriptFiles(turn.attachments, prefix), id),
      ...assistantTurn(
        str(turn.assistant_text),
        readTranscriptBlocks(turn.blocks, now, toContentBlock),
        readBlockOrder(turn.block_order),
        id,
      ),
    );
  }
  return turns;
}

/**
 * The live session's history, as the conversation showed it: a message a turn
 * took while it ran is a user entry where it entered the reply. A stopped reply
 * is kept, and marked, even when nothing of it streamed; any other empty entry
 * is left out. Each entry keeps its record's `turn_id`.
 */
function fromLive(
  session: SessionTranscript,
  sessionId: string,
  restoredAt?: number,
): TranscriptTurn[] {
  const entries = Array.isArray(session.turns) ? session.turns : [];
  return entries.flatMap((turn, index): TranscriptTurn[] => {
    const content = str(turn.content);
    const turnId = turnOf(turn.turn_id);
    const id = turnId !== null ? { turnId } : {};
    if (turn.role === 'user') {
      const prefix = `${sessionId}:${index.toString()}`;
      return userTurn(content, readTranscriptFiles(turn.attachments, prefix), id);
    }
    if (turn.role !== 'assistant') return [];
    return assistantTurn(
      content,
      readTranscriptBlocks(turn.blocks, restoredAt, toContentBlock),
      readBlockOrder(turn.block_order),
      turn.stopped === true ? { stopped: true, ...id } : id,
    );
  });
}

/** A JSON answer to a request that ran no turn, onto the seam. */
function toTurnAnswer(answer: MessageAnswer): TurnAnswer {
  if (answer.status === 'queued') return { status: 'queued' };
  if (answer.status === 'running') return { status: 'running' };
  if (answer.status === 'refused') return { status: 'refused', message: str(answer.message) };
  // `empty`, or a status this client does not know: either way no turn ran.
  return { status: 'empty' };
}

/**
 * A 409 that refused a request before any stream, its attach step's included: a turn or an
 * operation holds the session (`turn_in_progress`), or its queue is full (`queue_full`).
 */
function refusalAnswer(err: unknown): TurnAnswer | undefined {
  if (!(err instanceof OrchestratorError) || err.status !== 409) return undefined;
  if (err.message === 'turn_in_progress') return { status: 'busy' };
  if (err.message === 'queue_full') return { status: 'full' };
  return undefined;
}

/**
 * One request's chunks: its turn's stream mapped onto the seam or, when the
 * request ran no turn and was answered in JSON, one terminal chunk with that
 * answer. Throws before any chunk when the orchestrator refuses the request.
 * Its TURN_MODE is read only when `readsMode` (see thinkingFields), naming `ranModel` as what
 * the answer ran on.
 */
async function* openTurn(
  sessionId: string,
  body: MessageRequest,
  readsMode: boolean,
  ranModel: RanWith['model'],
  signal?: AbortSignal,
): AsyncIterable<StreamChunk> {
  // Every request says which visual blocks this build draws: messages, drains and card
  // answers alike, so a card's continuation can carry the visuals of its result.
  const open = streamMessage(
    sessionId,
    { ...body, client_context: { ...body.client_context, ...renderContext() } },
    signal,
  );
  // Trigger the fetch + ensureOk (throws here on 404/403/409, before any event).
  const first = await open.next();
  if (first.done && first.value) {
    yield { delta: '', done: true, answer: toTurnAnswer(first.value) };
    return;
  }
  const resume = async function* (): AsyncGenerator<SseEvent> {
    if (!first.done) yield first.value;
    yield* open;
  };
  yield* mapStream(resume(), sessionId, readsMode, ranModel);
}

/**
 * Sends a message, recovering from a stale session: if the orchestrator
 * rejects the session (404 not-found / 403 wrong-owner) BEFORE any event has
 * streamed, create a fresh session and retry once. The new session id is
 * reported via the first StreamChunk so the store rebinds it. A request
 * refused because a turn holds the session, or its queue is full, ends in one
 * terminal chunk saying so. `readsMode`: the turn's TURN_MODE is read (see thinkingFields);
 * on a card answer, only on a stop card's "Something else" (see cardFields). `ranModel`: the
 * request's model as this client resolves it, for the answer's meta line.
 * `closedOnRefusal` (a Connect card's answer alone): any other 409 answers `closed`.
 */
async function* sendWithRecovery(
  sessionId: string,
  body: MessageRequest,
  signal?: AbortSignal,
  opts: {
    retryOnStaleSession?: boolean;
    readsMode?: boolean;
    ranModel?: RanWith['model'];
    closedOnRefusal?: boolean;
  } = {},
): AsyncIterable<StreamChunk> {
  const { retryOnStaleSession = true, readsMode = false, ranModel, closedOnRefusal = false } = opts;
  try {
    yield* openTurn(sessionId, body, readsMode, ranModel, signal);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    const refused =
      refusalAnswer(err) ??
      (closedOnRefusal && err instanceof OrchestratorError && err.status === 409
        ? ({ status: 'closed' } as const)
        : undefined);
    if (refused) {
      yield { delta: '', done: true, answer: refused };
      return;
    }
    const status = err instanceof OrchestratorError ? err.status : 0;
    if (!retryOnStaleSession || (status !== 404 && status !== 403)) {
      yield { delta: '', done: true, error: true };
      return;
    }
    // Stale session — start a fresh one and retry the same message once. Carry
    // the same model choice from `body` so the retry runs on the picked model.
    const fresh = await createSession({ model: body.model, language: body.language }, signal);
    yield* openTurn(fresh, body, readsMode, ranModel, signal);
  }
}
